import { z } from "zod";

// Schedule provider types
export const ScheduleProviderSchema = z.enum(["global_tick", "eventbridge"]);
export type ScheduleProvider = z.infer<typeof ScheduleProviderSchema>;

// Schedule preset types for common patterns
export const SchedulePresetSchema = z.enum(["hourly", "daily", "weekly", "monthly", "first_monday", "custom"]);
export type SchedulePreset = z.infer<typeof SchedulePresetSchema>;

// Window configuration for complex schedules
export const ScheduleWindowConfigSchema = z.object({
  // Hours of day to run (0-23), e.g., [8, 9, 10, 11, 12, 13, 14, 15, 16, 17] for 8am-5pm
  hours: z.array(z.number().min(0).max(23)).optional(),
  // Days of week (0=Sunday, 6=Saturday), e.g., [1, 2, 3, 4, 5] for Mon-Fri
  daysOfWeek: z.array(z.number().min(0).max(6)).optional(),
  // Day of month (1-31)
  daysOfMonth: z.array(z.number().min(1).max(31)).optional(),
  // Week of month for patterns like "first Monday" (1-5)
  weekOfMonth: z.number().min(1).max(5).optional(),
});
export type ScheduleWindowConfig = z.infer<typeof ScheduleWindowConfigSchema>;

// Schedule configuration for scheduled triggers
export const ScheduleConfigSchema = z.object({
  // Provider: global_tick (DB-scanned) or eventbridge (AWS managed)
  provider: ScheduleProviderSchema.default("global_tick"),
  // IANA timezone (e.g., "America/New_York", "Europe/London")
  timezone: z.string().default("UTC"),
  // Preset for common patterns
  preset: SchedulePresetSchema.optional(),
  // Schedule type
  scheduleType: z.enum(["rate", "cron", "window"]).optional(),
  // For rate-based schedules
  rateValue: z.number().positive().optional(),
  rateUnit: z.enum(["minute", "hour", "day", "week", "month"]).optional(),
  // Raw cron expression (standard 5-field cron)
  cronExpression: z.string().optional(),
  // Window configuration for complex patterns
  windowConfig: ScheduleWindowConfigSchema.optional(),
  // Time of day for daily/weekly/monthly presets
  time: z
    .object({
      hour: z.number().min(0).max(23),
      minute: z.number().min(0).max(59),
    })
    .optional(),
});
export type ScheduleConfig = z.infer<typeof ScheduleConfigSchema>;

export const ActionNodeSchema = z.object({
  id: z.string(),
  nodeId: z.string().optional(),
  type: z.literal("action"),
  label: z.string().optional(),
  parentNode: z.string().optional(),
  position: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
  data: z.object({
    label: z.string(),
    note: z.string().optional(),
    readableId: z.string(),
    parentNode: z.string().optional(),
    input_data: z.any().optional(),
    app: z.object({
      id: z.string(),
      origin_id: z.string(),
      installed_id: z.string().optional(),
      name: z.string(),
      logo_url: z.string().nullable().optional(),
    }),
    action: z
      .object({
        id: z.string(),
        name: z.string(),
        description: z.string(),
        input_schema: z.any().optional(),
        output_schema: z.any().optional(),
        parameters: z
          .array(
            z.object({
              location: z.enum(["arguments", "body", "query", "any", "formData", "header", "path"]),
              input: z.any(),
            }),
          )
          .optional(),
      })
      .optional(),
  }),
});
export type ActionNode = z.infer<typeof ActionNodeSchema>;

export const TriggerNodeSchema = z.object({
  id: z.string(),
  parentNode: z.string().optional(),
  nodeId: z.string().optional(),
  type: z.literal("trigger"),
  label: z.string().optional(),
  position: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
  data: z.object({
    // Extended to include "schedule" for time-based recurring triggers
    triggerType: z.enum(["manual", "interval", "webhook", "event", "email", "schedule", "app"]),
    readableId: z.string(),
    // App trigger fields
    triggerId: z.string().optional(),
    triggerAppId: z.string().optional(),
    triggerInputs: z.record(z.string(), z.any()).optional(),
    // Email trigger fields
    allowedEmails: z.string().optional(),
    triggerEmail: z.string().optional(),
    // Event trigger fields
    source: z.string().optional(),
    event: z
      .object({
        record: z.string(),
        events: z.array(z.string()),
        runMode: z.enum(["automatic", "manual"]).default("automatic"),
      })
      .optional(),
    label: z.string(),
    note: z.string().optional(),
    recordId: z.string().optional(),
    recordTypes: z.array(z.string()).optional(),
    // Webhook trigger fields
    webhookToken: z.string().optional(),
    webhookUrl: z.string().optional(),
    runtime: z.enum(["sync", "async"]).optional(),
    authenticationHeader: z
      .object({
        required: z.boolean(),
        value: z.string(),
        key: z.string(),
      })
      .optional(),
    input_schema: z.any().optional(),
    // Schedule trigger fields
    schedule: ScheduleConfigSchema.optional(),
    intervalValue: z.number().optional(),
    intervalUnit: z.enum(["minutes", "hours", "days", "weeks", "months"]).optional(),
  }),
});
export type TriggerNode = z.infer<typeof TriggerNodeSchema>;

export const ConditionNodeSchema = z.object({
  id: z.string(),
  parentNode: z.string().optional(),
  nodeId: z.string().optional(),
  type: z.literal("condition"),
  label: z.string().optional(),
  position: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
  data: z.object({
    label: z.string(),
    note: z.string().optional(),
    readableId: z.string(),
    parentNode: z.string().optional(),
    action: z.object({
      parameters: z.array(
        z.object({
          location: z.enum(["arguments"]),
          input: z.object({ expression: z.string().optional() }).optional(),
        }),
      ),
    }),
  }),
});
export type ConditionNode = z.infer<typeof ConditionNodeSchema>;

export const IteratorNodeSchema = z.object({
  id: z.string(),
  type: z.literal("iterator"),
  parentNode: z.string().optional(),
  nodeId: z.string().optional(),
  label: z.string().optional(),
  position: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
  data: z.object({
    readableId: z.string(),
    parentNode: z.string().optional(),
    label: z.string().optional(),
    note: z.string().optional(),
    childNodes: z.string().array().optional(),
    action: z.object({
      input_schema: z.any().optional(),
      output_schema: z.any().optional(),
      parameters: z.any().optional(),
    }),
  }),
});
export type IteratorNode = z.infer<typeof IteratorNodeSchema>;

export const EntityBatchNodeTypeSchema = z.enum([
  "candidateRecordBatch",
  "contactRecordBatch",
  "clientRecordBatch",
  "jobRecordBatch",
  "applicationRecordBatch",
  "customEntityRecordBatch",
  "recordBatch",
]);

export const EntityBatchNodeSchema = z.object({
  id: z.string(),
  type: EntityBatchNodeTypeSchema,
  parentNode: z.string().optional(),
  nodeId: z.string().optional(),
  label: z.string().optional(),
  position: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
  data: z.object({
    readableId: z.string(),
    parentNode: z.string().optional(),
    label: z.string().optional(),
    note: z.string().optional(),
    childNodes: z.string().array().optional(),
    action: z.object({
      input_schema: z.any().optional(),
      output_schema: z.any().optional(),
      parameters: z.any().optional(),
    }),
  }),
});
export type EntityBatchNode = z.infer<typeof EntityBatchNodeSchema>;

export const DelayNodeSchema = z.object({
  id: z.string(),
  nodeId: z.string().optional(),
  parentNode: z.string().optional(),
  type: z.literal("delay"),
  label: z.string().optional(),
  position: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
  data: z.object({
    label: z.string(),
    note: z.string().optional(),
    readableId: z.string(),
    parentNode: z.string().optional(),
    action: z
      .object({
        parameters: z.array(z.object({ location: z.enum(["arguments"]), input: z.any() })).optional(),
      })
      .optional(),
  }),
});
export type DelayNode = z.infer<typeof DelayNodeSchema>;

export const EdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  sourceHandle: z.enum(["truthy", "falsy"]).nullable().optional(),
});
export type Edge = z.infer<typeof EdgeSchema>;

export const NodeSchema = z.discriminatedUnion("type", [
  TriggerNodeSchema,
  ActionNodeSchema,
  ConditionNodeSchema,
  IteratorNodeSchema,
  EntityBatchNodeSchema,
  DelayNodeSchema,
]);
